<style></style>

<template>
    <div class="page-container">
        <h1>Step 3: Take Action</h1>
        <section-collapse accordion="step3takeaction-accordion" id="enrollment-why-its-important">
            <template #title>Why it's Important</template>
            <template #collapsed-content>
                <p>
                    Working through action plans to reach selected goals often takes 1-3 months. During this time, child care providers get support from you and the Go NAPSACC tips & materials library. Your gentle prompts hold them accountable and keep things moving forward. Your knowledge of helpful community resources and success stories from other child care providers help them overcome challenges. Your support in learning how to navigate the tips & materials library builds their confidence and independence in improving healthy practices.
                </p>
            </template>
        </section-collapse>

        <section-collapse accordion="step3takeaction-accordion" id="enrollment-activities">
            <template #title>Activities</template>
            <template #collapsed-content>
                <p>Click any activity to read more</p>

                <activity-collapse>
                    <template #number>1</template>
                    <template #title>
                        <strong>Child care providers</strong> work toward their best practice goals
                    </template>
                    <template #collapsed-content>
                        <div>
                            <p>Child care providers should use their customized action plans to monitor their progress and to check off steps as they are completed. Resources from the tips & materials library offers great information, educational materials, classroom resources for teachers, and handouts for families.</p>
                        </div>
                        <provider-tools-spotlight class="mt-4">
                            <slot>
                                <h4>To access tips & materials…</h4>
                                <ol start="1" class="list-left-aligned">
                                    <li>Direct child care providers to the Tips & Materials page.</li>
                                    <li>Find the relevant module and click on the arrow beside Select a Category to see a drop-down list of all the sections. Click on the section of interest to see related tips & materials.</li>
                                </ol>
                                <img :src="$basePath + '/images/ta-resources/3-take-action/14 Step3TakeAction-Provider-To access tips and materials-01.png'" class="w-100 mb-4" style="margin-left: -12px">
                                <ol start="3" class="list-left-aligned">
                                    <li>Resources are grouped by their purpose or target audience.</li>
                                </ol>
                                <img :src="$basePath + '/images/ta-resources/3-take-action/15 Step3TakeAction-Provider-To access tips and materials-02.png'" class="w-100 mb-4" style="margin-left: -12px">
                            </slot>
                        </provider-tools-spotlight>
                    </template>


                </activity-collapse>
                <activity-collapse>
                    <template #number>2</template>
                    <template #title>
                        <strong>Consultant</strong> provides help and support
                    </template>
                    <template #collapsed-content>
                        <div>
                           <p>Monthly check-ins are a helpful to provide ongoing support to child care providers as they work through their action plans. Checking in by phone allows for a back-and-forth discussion. If phone calls are not possible, a quick email or text lets them know that you are thinking of them and are there for support.</p>
                        </div>
                        <h5>Check-in Prep:</h5>
                        <ul>
                            <li>Use your Consultant Tools to monitor progress. Orient yourself to the goals selected and make sure that an action plan has been created for each goal. Review action plan steps and see if any are marked as complete.</li>
                            <li>Review your notes from your last check-in for more context. Make sure that you followed-up on anything you offered to the child care provider, such as additional community resources or professional development options.  You are modeling that this is an important project and that everyone needs to be held accountable.</li>
                            <li>Use your Add TA Activity button to save any notes or questions needed for the check-in. Mark the Activity Type as Planning Notes.</li>
                        </ul>
                        <consultant-tools-spotlight>
                            <slot>
                                <h4>To monitor progress...</h4>
                                <ol start="1" class="list-left-aligned">
                                    <li>Go to your My Programs page.</li>
                                    <li>Click on the child care program’s name.</li>
                                </ol>
                                <img :src="$basePath + '/images/ta-resources/3-take-action/16 Step3TakeAction-Consultant-To monitor progress-01.png'" class="w-100 mb-4">
                                <ol start="3" class="list-left-aligned">
                                    <li>You see a dashboard very similar to their My NAPSACC page.</li>
                                    <li>Scroll down the page to the Plan section to see what goals have been set. Click on View/Update Action Plan to see the details of each action plan.</li>
                                    <li>Use the Add TA Activity button at the top right corner of the page to make a note.</li>
                                </ol>
                                <img :src="$basePath + '/images/ta-resources/3-take-action/17 Step3TakeAction-Consultant-To monitor progress-02.png'" class="w-100 mb-4">
                                <ol start="6" class="list-left-aligned">
                                    <li>Under Activity Type, mark your entry as Planning Notes.</li>
                                    <li>Record key information or reminders in the notes box.</li>
                                </ol>
                                <img :src="$basePath + '/images/ta-resources/3-take-action/18 Step3TakeAction-Consultant-To monitor progress-03.png'" class="w-100 mb-4">
                            </slot>
                        </consultant-tools-spotlight>

                        <h5>Check-in Agenda:</h5>
                            <strong class="text-primary">Inquire about progress</strong>
                            <ul class="fa-ul">
                                <li>"How has your work toward your goals been going?"</li>
                            </ul>

                            <strong class="text-primary">Encourage reflection</strong>
                            <p>Be mindful that some child care providers will be making good progress, while others may be struggling.</p>
                            <ul class="fa-ul">
                                <li>"What have been your successes over the past [month]? Have you come across any challenges in the steps you’ve taken so far? How are you feeling about meeting your goals by [date]?" </li>
                            </ul>
                            <p>In later cycles, check in about maintenance of past goals.</p>

                            <strong class="text-primary">Praise progress</strong>
                            <p>Make sure that action plans are updated to reflect steps completed or goals reached.</p>

                            <strong class="text-primary">Promote resources</strong>
                            <p>Suggest relevant tips & materials that can help with current challenges. Share success stories that you have heard from other child care providers working on similar goals.</p>

                            <strong class="text-primary">Discuss next steps</strong>
                            <p>For any incomplete goals, review next steps.</p>
                            <ul class="fa-ul">
                                <li>"What concerns do you have about completing your next steps?"</li>
                                <li>"What support do you need from me?"</li>
                            </ul>
                            <p>Set a date for the next check-in.</p>

                            <p><strong>After the check-in</strong>, document your interaction and any notes using the Add TA Activity button.</p>


                        <helpful-hints>
                            <h5>Build motivation</h5>
                            <p>Starting with easier goals can help child care providers see progress more quickly and provide momentum to tackle those more difficult goals. Even when progress is slow, be sure to recognize and praise any steps they have made toward any of their goals.</p>
                            <h5>Keep attention focused</h5>
                            <p>Help child care providers stay focused on goals despite busy schedules and competing priorities. In between check-ins, consider using email and text messages to offer quick reminders about next steps or upcoming deadlines. Sharing a related resource or piece of news is a way to show you are thinking of them and the work they are doing.</p>
                            <h5>Offer in-person support (if possible)</h5>
                            <p>Offer to come by during lunch or movement time so you can see the healthy practices in action and perhaps even model and provide suggestions. When offering this type of support, be sure to reassure child care providers that you are only there to provide help and support (not judgement). You want them to see you as a resource and mentor.</p>
                        </helpful-hints>
                    </template>
                </activity-collapse>

            </template>
        </section-collapse>

       <section-collapse accordion="step2assess-accordion" id="enrollment-common-challenges">
            <template #title>Common Challenges & Solutions</template>
            <template #collapsed-content>
                <h4 class="mt-0">Forgotten username or password</h4>
                <p>Go NAPSACC can help retrieve username/email and reset password. The child care provider clicks on the blue Log In button at the top of the homepage. When the Log In box appears, they can click on Forgot Your Email or Forgot Your Password.</p>
                <ul>
                    <li>Forgot Your Email allows the child care provider to look up the email address they registered with based on their name and the name and location of their child care program.</li>
                    <li>Forgot Your Password sends an email to the child care provider with a link and instructions for creating a new password.</li>
                    <li>If they are no longer able to access the email account listed and are unable to get logged in, contact Go NAPSACC at <a href="mailto:gonapsacc@unc.edu">gonapsacc@unc.edu</a> or (919)-843-3863. We try to respond as quickly as possible, but it is not always immediate.</li>
                </ul>
                <h4 class="mt-0">Difficulty reaching child care providers for check-ins</h4>
                <p>Child care providers are busy and may avoid contact if they have not made progress and/or are no longer interested in participating. When phone, text, and email are not working, you may want to try dropping by for an in-person visit. </p>
                    
            </template>
        </section-collapse>

        <section-collapse accordion="step2assess-accordion" id="enrollment-support-documents">
            <template #title>Support Documents</template>
            <template #collapsed-content>
                <slot name="support-docs"><div>some backup text for support docs in enrollment</div></slot>
            </template>
        </section-collapse>

    </div>
</template>

<script>
    import SectionCollapse from '../../TaResources/SectionCollapse.vue';
    import HelpfulHints from '../../TaResources/HelpfulHints.vue';
    import ActivityCollapse from '../../TaResources/ActivityCollapse.vue';
    import ConsultantToolsSpotlight from '../../TaResources/ConsultantToolsSpotlight.vue';
    import ProviderToolsSpotlight from '../../TaResources/ProviderToolsSpotlight.vue';

    export default {
        components: {
            SectionCollapse,
            HelpfulHints,
            ActivityCollapse,
            ConsultantToolsSpotlight,           
            ProviderToolsSpotlight, 
        },
        data() {
            return {}
        },
        methods: {}
    
}
</script>